/**
 * Keeps the app alive when the browser's own translator is running.
 *
 * Chrome and Safari translate a page by replacing its text nodes with
 * <font> wrappers of their own. React still holds references to the original
 * text nodes, so the next time it swaps a conditional branch it calls
 * removeChild or insertBefore with a node that no longer sits where React
 * thinks it does. The DOM throws NotFoundError, the error boundary catches it
 * and the coach gets "Something went wrong!" in the middle of a screening.
 * This is React's long-standing issue #11538 and it has no fix inside React.
 *
 * The accepted workaround is to make those two calls defensive: when the node
 * is not where it is claimed to be, the translator already moved or removed it,
 * so doing nothing leaves the page in the state the translator produced rather
 * than tearing the whole route down. The visible text may be a beat stale until
 * the next render; a stale label beats a dead screening.
 *
 * Call once, before React mounts.
 */
export function installMachineTranslationSafety(): void {
  if (typeof Node !== "function" || !Node.prototype) {
    return;
  }

  const proto = Node.prototype as Node & { __bgTranslationSafe?: boolean };
  if (proto.__bgTranslationSafe) {
    return;
  }
  proto.__bgTranslationSafe = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildSafely<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      // The translator reparented it. It is already gone from our subtree.
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeSafely<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      // The anchor we were given has been moved. Append instead of throwing.
      return originalInsertBefore.call(this, node, null) as T;
    }
    return originalInsertBefore.call(this, node, reference) as T;
  };
}
